import React, { useRef, useEffect, useCallback } from 'react';
import { useGame } from '../context/GameContext.jsx'; 
import QrScannerModal from './QrScannerModal.jsx'; 
import TopBar from './TopBar.jsx'; 
import Card from './Card.jsx'; 
import Category from './Category.jsx'; 
import BottomBar from './BottomBar.jsx';

const GameScreen = () => {
  const {
    sessionId,
    currentCounter,
    currentCards,
    translations,
    lang,
    handleQrCodeScanned,
    isLocalhost,
    setError,
    qrScannerModalRef, 
    navigateToCardInCarousel, 
    lastScannedCardCode, 
    setLastScannedCardCode, 
    isLoading,
    fetchSessionStatus,
    gameStatus,
    makeApiCall,
    setCurrentCounter,
    handleOutcome,
    cardLang,
    isPollingUpdate,
    setIsPollingUpdate
  } = useGame();

  const carouselRef = useRef(null); 
  const hurryUpIntervalRef = useRef(null);

  useEffect(() => {
    if (!sessionId && !isLoading && window.location.hash.substring(1) !== 'outcome') {
      console.log('GameScreen: No session ID and not on outcome screen, redirecting to initial screen.');
      window.location.hash = 'initial';
    }
  }, [sessionId, isLoading, gameStatus, lang, translations]);

  useEffect(() => {
    if (sessionId && currentCards.length === 0 && !isLoading && window.location.hash.substring(1) !== 'outcome') {
      console.log('GameScreen: Loading session data automatically due to refresh or direct entry.');
      fetchSessionStatus();
    }
  }, [sessionId, currentCards.length, isLoading, fetchSessionStatus, gameStatus, lang, translations]);

  useEffect(() => {
    let bsCarouselInstance = null;
    const carouselElement = carouselRef.current; 

    if (carouselElement) {
      bsCarouselInstance = window.bootstrap.Carousel.getInstance(carouselElement) || new window.bootstrap.Carousel(carouselElement, { interval: false });

      const handleNavigate = (event) => {
        if (bsCarouselInstance) {
          bsCarouselInstance.to(event.detail.index); 
        }
      };
      if (window.location.hash.substring(1) !== 'outcome') {
        window.addEventListener('navigateToCarouselItem', handleNavigate);
      }

      return () => {
        if (window.location.hash.substring(1) !== 'outcome') {
          window.removeEventListener('navigateToCarouselItem', handleNavigate);
        }
      };
    }
  }, [currentCards]);

  useEffect(() => {
    if (isPollingUpdate) {
      console.log('GameScreen: Skipping carousel navigation due to polling update.');
      return;
    }
    if (currentCards.length > 0 && (gameStatus === 'mission' || gameStatus === 'game')) {
      let targetCardId = null;
      if (lastScannedCardCode) {
        targetCardId = lastScannedCardCode;
      } else {
        targetCardId = currentCards[currentCards.length - 1].CardId;
      }

      const cardToNavigate = currentCards.find(card => card.CardId === targetCardId);
      if (cardToNavigate) {
        navigateToCardInCarousel(cardToNavigate.CardId);
        setLastScannedCardCode(null);
      }
    }
  }, [lastScannedCardCode, currentCards, navigateToCardInCarousel, setLastScannedCardCode, gameStatus, isPollingUpdate]);

  useEffect(() => {
    const missionCard = currentCards.find(card => card.Type && card.Type.toLowerCase() === "mission");
    let hurryUpInterval = 0;

    if (missionCard && missionCard.Modifiers && missionCard.Modifiers.includes("hurry_up")) {
      const intervalModifier = missionCard.Modifiers.find(modifier => modifier.startsWith("interval_"));
      if (intervalModifier) {
        const intervalValue = parseInt(intervalModifier.replace("interval_", "").replace("s", ""), 10);
        if (!isNaN(intervalValue) && intervalValue > 0) {
          hurryUpInterval = intervalValue;
        }
      }
      if (hurryUpInterval === 0) {
        hurryUpInterval = 30;
      }

      if (hurryUpIntervalRef.current) {
        clearInterval(hurryUpIntervalRef.current);
      }

      hurryUpIntervalRef.current = setInterval(async () => {
        if (!sessionId) {
          clearInterval(hurryUpIntervalRef.current);
          hurryUpIntervalRef.current = null;
          console.warn('HurryUp polling stopped: No session ID.');
          return;
        }
        await fetchSessionStatus(true, true, true);
      }, hurryUpInterval * 1000);
    } else {
      if (hurryUpIntervalRef.current) {
        clearInterval(hurryUpIntervalRef.current);
        hurryUpIntervalRef.current = null;
      }
    }

    return () => {
      if (hurryUpIntervalRef.current) {
        clearInterval(hurryUpIntervalRef.current);
        hurryUpIntervalRef.current = null;
      }
    };
  }, [currentCards, sessionId, fetchSessionStatus]);


  const renderCarouselItems = useCallback(() => {
    return currentCards.map((card, index) => (
      <div key={card.CardId} className={`carousel-item ${index === currentCards.length - 1 ? 'active' : ''}`}>
        <Card card={card} isMissionCard={false} /> 
      </div>
    ));
  }, [currentCards]);

  return (
    window.location.hash.substring(1) !== 'outcome' ? (
      <div className="screen-visible game-screen-content"> 
        <TopBar currentCounter={currentCounter} /> 
        
        <div id="cardCarousel" className="carousel slide bg-dark rounded p-3 mb-4" data-bs-interval="false" ref={carouselRef}>
          <div className="carousel-inner">{renderCarouselItems()}</div>
          <button className="carousel-control-prev" type="button" data-bs-target="#cardCarousel" data-bs-slide="prev">
            <span className="carousel-control-prev-icon" aria-hidden="true"></span>
            <span className="visually-hidden">{translations[lang]?.previous_button_label}</span>
          </button>
          <button className="carousel-control-next" type="button" data-bs-target="#cardCarousel" data-bs-slide="next">
            <span className="carousel-control-next-icon" aria-hidden="true"></span>
            <span className="visually-hidden">{translations[lang]?.next_button_label}</span>
          </button>
        </div>

        {isLocalhost && (
          <div className="mt-4 p-3 border rounded bg-secondary">
            <h5 className="text-light">{translations[lang]?.simulate_qr_title}</h5>
            <div className="input-group">
              <input type="text" id="simulatedQrInput" className="form-control" placeholder={translations[lang]?.simulated_qr_placeholder} />
              <button className="btn btn-primary" type="button" onClick={() => {
                const simulatedCode = document.getElementById('simulatedQrInput').value.trim();
                if (simulatedCode) {
                  handleQrCodeScanned(simulatedCode);
                  document.getElementById('simulatedQrInput').value = '';
                } else {
                  setError(translations[lang]?.empty_simulated_qr_error);
                }
              }}>
                {translations[lang]?.simulate_button}
              </button>
            </div>
          </div>
        )}

        <div className="row mt-4 g-3">
          <Category type="P" /> 
          <Category type="L" /> 
          <Category type="E" /> 
        </div>

        <QrScannerModal />
        <BottomBar />
      </div>
    ) : null
  );
};

export default GameScreen;
